import { Trans } from '@lingui/macro';
import { Typography } from '@material-ui/core';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { generateIntervals, generateTimes, getUserTzAbbr } from '../../ServiceTestsScheduleGenerators';
import useScheduleFormBridge from '../hooks/UseScheduleFormBridge';
import ScheduleFields from './ScheduleFields';
import { useTestTypeContext } from '../hooks/UseTestTypeContext';
import { MULTI } from '../../../general/Constants';
import TestFormSection from './TestFormSection/TestFormSection';
import { ADAPTER_TYPE_FIELD_NAME } from '../../ServiceTestsConstants';

/**
 * Renders the Run Schedule section in the test detail form.
 *
 * Provides interval/time/timezone data from testTypeObject context,
 * uses UseScheduleFormBridge to populate flat schedule fields from the
 * parent form's nested scheduling object, and renders ScheduleFields.
 *
 * When adapterType is 'multi' (child of Wi-Fi Group Test), shows a
 * parent-reference message instead of schedule controls.
 *
 * Requires a react-hook-form FormProvider ancestor (provided by InfrastructureDetailBase).
 */
const TestRunScheduleSection = () => {
    const { testType } = useTestTypeContext();
    const { watch } = useFormContext();
    const adapterType = watch(ADAPTER_TYPE_FIELD_NAME);
    const isMultiAdapter = adapterType === MULTI;

    // generateIntervals filters ALL_TEST_INTERVALS by the test type's allowed range.
    // testType?.scheduling?.interval is { min, max } in seconds.
    const intervals = useMemo(() => generateIntervals(testType?.scheduling?.interval, testType), [testType]);
    const times = useMemo(() => generateTimes(), []);
    const tzAbbr = useMemo(() => getUserTzAbbr(), []);

    // Bridges parent form's nested scheduling API object to flat ScheduleFields.
    useScheduleFormBridge({ intervals });

    return (
        <TestFormSection title={<Trans>Run Schedule</Trans>}>
            {isMultiAdapter ? (
                <Typography variant="body2">
                    <Trans>This test will use the schedule specified in its parent Wi-Fi Group Test</Trans>
                </Typography>
            ) : (
                <ScheduleFields intervals={intervals} times={times} tzAbbr={tzAbbr} />
            )}
        </TestFormSection>
    );
};

export default TestRunScheduleSection;
